import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Pin, PinOff, Plus, StickyNote } from 'lucide-react';
import Button from '../ui/Button';
import FollowUpCard from './FollowUpCard';
import FollowUpForm, { type CrmChoice } from './FollowUpForm';
import { CrmFileList, PhotoPicker } from './CrmPhotos';
import { uploadAll } from './crmUpload';
import salesService from '../../services/sales';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { subjectLinks } from '../../utils/crm';
import type { CrmAttach, CrmSubject, CustomerNote, FollowUp, FollowUpKind, SalesPerson } from '../../types/sales';

/**
 * Notes, follow-ups and issues about one customer, order or load (a Sales
 * seat, or a Nursery operator and up). A load's lists hold its orders' too,
 * and an order's are on its customer's. Notes take photos; a pinned note on
 * a customer shows on the incoming-call card when they ring. Internal only.
 */

function NoteItem({ note, tz, showing, onChange, onRemoved }: {
  note: CustomerNote; tz: string; showing: { customer?: boolean; order?: boolean; load?: boolean };
  onChange: (n: CustomerNote) => void; onRemoved: (id: number) => void;
}) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };
  const save = (changes: { body?: string; pinned?: boolean }) => run(async () => {
    const res = await salesService.updateNote(note.id, changes);
    if (!res.success || !res.data) throw res;
    onChange(res.data);
    setEditing(false);
  });
  const remove = () => run(async () => {
    const res = await salesService.removeNote(note.id);
    if (!res.success) throw res;
    onRemoved(note.id);
  });
  const removePhoto = (fileId: number) => run(async () => {
    const res = await salesService.removeFile(fileId);
    if (!res.success) throw res;
    onChange({ ...note, files: note.files.filter((f) => f.id !== fileId) });
  });
  const links = subjectLinks(note, showing);

  return (
    <li className={`p-4 min-w-0 ${note.pinned ? 'bg-amber-50' : ''}`}>
      <div className="flex items-start gap-3 min-w-0">
        <StickyNote className={`h-5 w-5 shrink-0 ${note.pinned ? 'text-amber-600' : 'text-neutral-400'}`} aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          {editing ? (
            <div className="space-y-2">
              <label htmlFor={`${id}-body`} className="sr-only">Note</label>
              <textarea id={`${id}-body`} value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={8000}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0" />
              <div className="flex gap-2">
                <Button size="sm" isLoading={busy} disabled={!body.trim()} onClick={() => save({ body: body.trim() })}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => { setBody(note.body); setEditing(false); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-neutral-800 whitespace-pre-wrap break-words">{note.body}</p>
          )}
          <CrmFileList files={note.files ?? []} onRemove={note.can_edit ? removePhoto : undefined} />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-xs text-neutral-600 break-words">
              {note.pinned && <span className="font-medium text-amber-800">Pinned · </span>}
              {note.created_by_name || 'Someone'}, {formatDateTimeInTz(note.created_at, tz)}
              {note.edited_at ? ` · edited${note.edited_by_name ? ` by ${note.edited_by_name}` : ''}` : ''}
              {note.voicemail_id ? ' · about a voicemail' : note.call_id ? ' · about a call' : ''}
              {links.map((l, i) => (
                <span key={`${l.label}-${i}`}>
                  {' · '}
                  {l.to ? <Link to={l.to} className="font-medium text-primary-700 underline">{l.label}</Link> : l.label}
                </span>
              ))}
            </p>
            {!editing && (
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => save({ pinned: !note.pinned })}>
                  {note.pinned ? <><PinOff className="h-4 w-4 mr-1" aria-hidden />Unpin</> : <><Pin className="h-4 w-4 mr-1" aria-hidden />Pin</>}
                </Button>
                {note.can_edit && (
                  <>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(true)}>Edit</Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={remove}>Remove</Button>
                  </>
                )}
              </div>
            )}
          </div>
          {error && <p className="text-sm text-red-700 break-words">{error}</p>}
        </div>
      </div>
    </li>
  );
}

/** Pinned first, then newest first: the order the server sends. */
function sortNotes(list: CustomerNote[]): CustomerNote[] {
  return [...list].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.created_at.localeCompare(a.created_at) || b.id - a.id);
}

function attachFor(subject: CrmSubject): CrmAttach {
  if ('customerId' in subject) return { customer_id: subject.customerId };
  if ('orderId' in subject) return { order_id: subject.orderId };
  return { load_id: subject.loadId };
}

export default function CrmPanel({ subject, lineChoices, orderChoices, heading }: {
  subject: CrmSubject;
  /** On an order: its lines, to say which one a note or issue is about. */
  lineChoices?: CrmChoice[];
  /** On a load: its orders, likewise. */
  orderChoices?: CrmChoice[];
  /** Shown above the panel on a page that is not all about this (an order, a load). */
  heading?: string;
}) {
  const tz = useOrgTimezone();
  const id = useId();
  const [notes, setNotes] = useState<CustomerNote[] | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[] | null>(null);
  const [people, setPeople] = useState<SalesPerson[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [noteLine, setNoteLine] = useState<number | ''>('');
  const [noteOrder, setNoteOrder] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [adding, setAdding] = useState<FollowUpKind | null>(null);

  const isCustomer = 'customerId' in subject;
  const showing = { customer: isCustomer, order: 'orderId' in subject, load: 'loadId' in subject };
  const key = JSON.stringify(subject);

  const load = useCallback(async () => {
    try {
      const s = JSON.parse(key) as CrmSubject;
      const [n, f] = await Promise.all([salesService.listNotes(s), salesService.listFollowUps('open', { subject: s })]);
      if (!n.success) throw n;
      if (!f.success) throw f;
      setNotes(n.data ?? []);
      setFollowUps(f.data ?? []);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the notes'));
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    salesService.listCrmPeople().then((r) => { if (r.success && r.data) setPeople(r.data); }).catch(() => {});
  }, []);

  const addNote = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    setNoteError(null);
    try {
      const res = await salesService.addNote({
        ...attachFor(subject),
        ...(noteLine !== '' ? { order_line_id: noteLine } : {}),
        ...(noteOrder !== '' ? { order_id: noteOrder } : {}),
        body: body.trim(),
        pinned,
      });
      if (!res.success || !res.data) throw res;
      const { saved, failed } = await uploadAll('notes', res.data.id, photos);
      const note = { ...res.data, files: [...(res.data.files ?? []), ...saved] };
      setNotes((list) => sortNotes([note, ...(list ?? [])]));
      setBody('');
      setPinned(false);
      setPhotos([]);
      setNoteLine('');
      setNoteOrder('');
      if (failed) setNoteError(`Saved, but ${failed === 1 ? 'a photo' : `${failed} photos`} could not be sent.`);
    } catch (err) {
      setNoteError(errorMessage(err, 'Could not save the note'));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="text-sm text-red-700 break-words">{error}</p>;
  if (!notes || !followUps) return <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />;

  const replaceFollowUp = (fu: FollowUp) => setFollowUps((list) => list && list.map((x) => (x.id === fu.id ? fu : x)));
  const open = followUps.filter((f) => f.status === 'open');
  const openIssues = open.filter((f) => f.kind === 'issue').length;
  const select = 'w-full sm:w-80 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0';

  return (
    <div className="space-y-4">
      {heading && <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{heading}</h2>}
      <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-neutral-100">
          <h3 className="text-base font-semibold text-neutral-900">
            Follow-ups and issues{open.length ? ` (${open.length} open${openIssues ? `, ${openIssues} ${openIssues === 1 ? 'issue' : 'issues'}` : ''})` : ''}
          </h3>
          {!adding && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setAdding('issue')}>
                <AlertTriangle className="h-4 w-4 mr-1" aria-hidden />Raise issue
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding('task')}>
                <Plus className="h-4 w-4 mr-1" aria-hidden />Add follow-up
              </Button>
            </div>
          )}
        </div>
        {adding && (
          <div className="p-4 border-b border-neutral-100">
            <FollowUpForm people={people} tz={tz} from={attachFor(subject)} defaultKind={adding}
              lineChoices={lineChoices} orderChoices={orderChoices}
              onSaved={(fu) => { setFollowUps((list) => [fu, ...(list ?? [])]); setAdding(null); }}
              onCancel={() => setAdding(null)} />
          </div>
        )}
        {followUps.length === 0 ? (
          !adding && <p className="p-4 text-sm text-neutral-600">Nothing to follow up, and no issues.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {followUps.map((f) => <FollowUpCard key={f.id} fu={f} people={people} tz={tz} onChange={replaceFollowUp} showing={showing} />)}
          </ul>
        )}
      </section>

      <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
        <h3 className="p-4 border-b border-neutral-100 text-base font-semibold text-neutral-900">Notes</h3>
        <form onSubmit={addNote} className="p-4 border-b border-neutral-100 space-y-2 min-w-0">
          <label htmlFor={`${id}-new`} className="block text-xs font-medium text-neutral-600">Add a note</label>
          <textarea id={`${id}-new`} value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={8000}
            placeholder="What was said, agreed, or is worth knowing next time"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0" />
          {lineChoices && lineChoices.length > 0 && (
            <div className="min-w-0">
              <label htmlFor={`${id}-line`} className="block text-xs font-medium text-neutral-600 mb-1">About a line (optional)</label>
              <select id={`${id}-line`} value={noteLine} onChange={(e) => setNoteLine(e.target.value ? Number(e.target.value) : '')} className={select}>
                <option value="">The whole order</option>
                {lineChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          )}
          {orderChoices && orderChoices.length > 0 && (
            <div className="min-w-0">
              <label htmlFor={`${id}-order`} className="block text-xs font-medium text-neutral-600 mb-1">About an order (optional)</label>
              <select id={`${id}-order`} value={noteOrder} onChange={(e) => setNoteOrder(e.target.value ? Number(e.target.value) : '')} className={select}>
                <option value="">The whole load</option>
                {orderChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          )}
          <PhotoPicker files={photos} onChange={setPhotos} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            {isCustomer ? (
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="rounded border-neutral-300" />
                Pin it (shown when they ring)
              </label>
            ) : <span />}
            <Button type="submit" size="sm" isLoading={saving} disabled={!body.trim()}>Save note</Button>
          </div>
          {noteError && <p className="text-sm text-red-700 break-words">{noteError}</p>}
        </form>
        {notes.length === 0 ? (
          <p className="p-4 text-sm text-neutral-600">No notes yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {notes.map((n) => (
              <NoteItem key={n.id} note={n} tz={tz} showing={showing}
                onChange={(next) => setNotes((list) => list && sortNotes(list.map((x) => (x.id === next.id ? next : x))))}
                onRemoved={(gone) => setNotes((list) => list && list.filter((x) => x.id !== gone))} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
